import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { webSocketService } from "@/services/websocket";
import { IncomingCallModal } from "./IncomingCallModal";
import { VideoCallPage } from "./VideoCallPage";
import type {
  IncomingCallData,
  CallAcceptedData,
  CallRejectedData,
  CallEndedData,
  MeetingContext,
} from "@/services/video-call";

interface VideoCallManagerProps {
  children: React.ReactNode;
}

export const VideoCallManager: React.FC<VideoCallManagerProps> = ({
  children,
}) => {
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(
    null
  );
  const [activeMeeting, setActiveMeeting] = useState<MeetingContext | null>(
    null
  );
  const isLeavingRef = useRef(false);
  const handlerIdRef = useRef<string | null>(null);
  const { isAuthenticated, token } = useAuth();

  // Initialize WebSocket connection based on auth state
  useEffect(() => {
    const initializeWebSocket = async () => {
      if (isAuthenticated && token) {
        console.log("Authenticated, initializing WebSocket for video calls...");
        try {
          await webSocketService.connect(token); // Use token from auth context

          // Set up video call event handlers
          handlerIdRef.current = webSocketService.addVideoCallHandlers({
            onIncomingCall: handleIncomingCall,
            onCallAccepted: handleCallAccepted,
            onCallRejected: handleCallRejected,
            onCallEnded: handleCallEnded,
            onError: handleWebSocketError,
          });

          console.log("WebSocket connected and video call handlers set");
          console.log("Connection status:", webSocketService.getConnectionStatus());
        } catch (error) {
          console.error("Failed to initialize WebSocket:", error);
        } finally {
        }
      }
    };

    initializeWebSocket();

    // Cleanup on unmount or when auth state changes
    return () => {
      if (handlerIdRef.current) {
        console.log("Removing video call handlers due to auth change or unmount.");
        webSocketService.removeVideoCallHandlers(handlerIdRef.current);
        handlerIdRef.current = null;
      }
      if (webSocketService.isConnected()) {
        console.log("Disconnecting WebSocket due to auth change or unmount.");
        webSocketService.disconnect();
      }
    };
  }, [isAuthenticated, token]); // Re-run when auth state changes

  const handleIncomingCall = (data: IncomingCallData) => {
    console.log("📞 Incoming call received:", data);
    setIncomingCall(data);
  };

  const handleCallAccepted = (data: CallAcceptedData) => {
    console.log("✅ Call accepted via WebSocket:", data);
    // This handles call_accepted events from WebSocket (e.g., when student accepts)
    // For counselor accepting calls, we use handleCallAcceptedLocal
    if (data.callId === incomingCall?.callId) {
      setIncomingCall(null);
      setActiveMeeting({
        meetingId: data.meetingId,
        token: data.token,
        callId: data.callId,
        participant: incomingCall?.from,
        roomId: data.roomId,
        callType: data.callType || incomingCall?.callType,
      });
    }
  };

  const handleCallRejected = (data: CallRejectedData) => {
    console.log("❌ Call rejected via WebSocket:", data);
    setIncomingCall(null);
  };

  const handleCallEnded = (data: CallEndedData) => {
    console.log("🔚 Call ended via WebSocket:", data);
    setIncomingCall(null);
    setActiveMeeting(null);
  };

  const handleWebSocketError = (error: string) => {
    console.error("🚨 WebSocket error in VideoCallManager:", error);
  };

  const handleCallModalClose = () => {
    setIncomingCall(null);
  };

  const handleCallAcceptedLocal = (callData: {
    callId: string;
    meetingId: string;
    roomId: string;
    token: string;
    callType?: 'audio' | 'video';
  }) => {
    console.log("✅ Call accepted locally by counselor:", callData);
    console.log("WebSocket status after call acceptance:", webSocketService.getConnectionStatus());
    setIncomingCall(null);
    setActiveMeeting({
      meetingId: callData.meetingId,
      token: callData.token,
      callId: callData.callId,
      participant: incomingCall?.from,
      roomId: callData.roomId,
      callType: callData.callType || incomingCall?.callType, // Pass call type from response or incoming call
    });
  };

  const handleCallRejectedLocal = () => {
    console.log("❌ Call rejected locally by counselor");
    setIncomingCall(null);
  };

  const handleLeaveMeeting = () => {
    if (isLeavingRef.current) {
      console.log("🚫 Already leaving meeting, ignoring duplicate call");
      return;
    }
    
    isLeavingRef.current = true;
    console.log("🚪 Counselor leaving meeting");
    setActiveMeeting(null);
    
    // Reset the flag after a short delay to allow for future calls
    setTimeout(() => {
      isLeavingRef.current = false;
    }, 1000);
  };

  // If there's an active meeting, show the video call page
  if (activeMeeting) {
    return (
      <VideoCallPage
        meetingContext={activeMeeting}
        onLeave={handleLeaveMeeting}
      />
    );
  }

  return (
    <div>
      {children}

      {/* Incoming Call Modal */}
      <IncomingCallModal
        isOpen={!!incomingCall}
        callData={incomingCall || undefined}
        onClose={handleCallModalClose}
        onCallAccepted={handleCallAcceptedLocal}
        onCallRejected={handleCallRejectedLocal}
      />
    </div>
  );
};
